<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
const { log, warn, error, debug } = useLogger()

const props = defineProps<{
  textToCopy: string
  buttonText?: string
  successText?: string
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}>()

const { toast } = useToast()
const copied = ref(false)
const copyError = ref(false)
const isLoading = ref(false)

const copyToClipboard = async () => {
  if (!props.textToCopy) return
  isLoading.value = true
  try {
    await navigator.clipboard.writeText(props.textToCopy)
    copied.value = true
    // Optional: Show a small success toast or rely on button text change
    // toast({ title: 'Copied!', description: 'Link copied to clipboard.', duration: 2000 });
    setTimeout(() => {
      copied.value = false // Reset after a short delay
    }, 2000)
  } catch (err) {
    error('Copy failed:', err)
    copyError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <Button
    variant="ghost"
    size="icon"
    :disabled="copied || copyError"
    class="relative w-8 h-8"
    @click="copyToClipboard"
  >
    <span class="sr-only">Copy</span>
    <transition
      enter-active-class="transition ease-in duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-out duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <Icon v-if="copied" name="heroicons:check-circle" class="h-4 w-4 mr-1.5 text-green-600" />
      <Icon v-else-if="isLoading" name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
      <Icon v-else name="heroicons:clipboard-document" class="h-4 w-4 mr-1.5" />
    </transition>
  </Button>
</template>
